/**
 * EdgeIndicators Component
 * Clickable edge indicators with compass direction labels
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 1109-1185, 77 lines)
 *
 * Features:
 * - Circular markers at edge midpoints
 * - Clickable to select edge for azimuth snapping
 * - Shows edge number (1-indexed)
 * - Rotated labels to match edge direction
 * - Yellow highlight for selected edge
 * - Only visible in select mode or when an edge is selected
 */

import React from 'react';
import { CircleMarker, Marker } from 'react-leaflet';
import L from 'leaflet';

export interface EdgeIndicatorsProps {
  /** Polygon coordinates [lat, lng][] */
  coords: [number, number][];

  /** Index of currently selected edge */
  selectedEdgeIndex: number;

  /** Whether in edge selection mode (shows all indicators) */
  isSelectMode: boolean;

  /** Callback when edge is clicked */
  onEdgeClick: (index: number) => void;
}

/**
 * Render clickable edge indicators for selecting azimuth snap edge
 *
 * Shows circular markers at edge midpoints with numbered labels.
 * In select mode, all edges are shown. Otherwise, only the selected edge is visible.
 * Labels rotate to match edge direction for intuitive visual feedback.
 */
export function EdgeIndicators({
  coords,
  selectedEdgeIndex,
  isSelectMode,
  onEdgeClick,
}: EdgeIndicatorsProps) {
  if (!isSelectMode && selectedEdgeIndex === undefined) return null;

  return (
    <>
      {coords.slice(0, -1).map((start, index) => {
        const end = coords[index + 1];
        const midLat = (start[0] + end[0]) / 2;
        const midLng = (start[1] + end[1]) / 2;
        const isSelected = index === selectedEdgeIndex;

        // Calculate angle for arrow rotation
        const angle = Math.atan2(end[1] - start[1], end[0] - start[0]) * (180 / Math.PI);

        return (
          <CircleMarker
            key={`edge-${index}`}
            center={[midLat, midLng]}
            // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
            radius={isSelected ? 12 : 8}
            pathOptions={{
              fillColor: isSelected ? '#EBFD03' : '#ffffff',
              color: isSelected ? '#000000' : '#666666',
              weight: 2,
              fillOpacity: isSelectMode ? 0.9 : (isSelected ? 0.9 : 0),
              opacity: isSelectMode ? 1 : (isSelected ? 1 : 0),
            }}
            eventHandlers={{
              click: (e: any) => {
                L.DomEvent.stopPropagation(e);
                onEdgeClick(index);
              },
            }}
          >
            {isSelectMode && (
              <Marker
                position={[midLat, midLng]}
                // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
                icon={L.divIcon({
                  className: 'edge-label',
                  html: `<div style="
                    background: ${isSelected ? '#EBFD03' : 'white'};
                    border: 2px solid ${isSelected ? 'black' : '#666'};
                    border-radius: 4px;
                    padding: 2px 6px;
                    font-size: 10px;
                    font-weight: bold;
                    white-space: nowrap;
                    transform: rotate(${angle}deg);
                    cursor: pointer;
                  ">${index + 1}</div>`,
                  iconSize: [30, 20],
                  iconAnchor: [15, 10],
                })}
                eventHandlers={{
                  click: (e: any) => {
                    L.DomEvent.stopPropagation(e);
                    onEdgeClick(index);
                  },
                }}
              />
            )}
          </CircleMarker>
        );
      })}
    </>
  );
}
